import { useState } from "react";
import { useBootstrapCsrf, useLogin, useMe } from "../api/api_hooks";
import { Link, useNavigate } from "react-router-dom";
import { AuthError, AuthField, AuthIcon, AuthLayout, AuthSubmitButton } from "../components/AuthLayout";
import { useRemotePage } from "../remote/RemoteControlContext";

export default function LoginPage() {
  useBootstrapCsrf();
  const { data: me } = useMe();
  const nav = useNavigate();
  const login = useLogin();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useRemotePage({
    id: "login",
    title: "Sign in",
    subtitle: "Enter credentials securely on the main screen",
    state: { signedIn: Boolean(me) },
    sections: [],
    actions: {},
  });

  const onSubmit = (event) => {
    event.preventDefault();
    login.mutate(
      { identifier, password },
      { onSuccess: () => nav("/") }
    );
  };

  const errorMessage = login.error?.response?.data?.non_field_errors?.[0]
    ?? login.error?.response?.data?.detail
    ?? "Unable to sign in. Check your email, username, and password.";

  if (me) {
    const playerName = me.username || me.email || `Player ${me.id}`;
    return (
      <AuthLayout mode="login">
        <div className="py-8 text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 text-2xl font-black shadow-xl shadow-fuchsia-950/40">
            {String(playerName).charAt(0).toUpperCase()}
          </span>
          <p className="mt-6 text-xs font-black uppercase tracking-[0.3em] text-cyan-200/70">Session active</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-white">You&apos;re already in.</h1>
          <p className="mt-3 text-indigo-100/55">Signed in as <strong className="text-indigo-100">{playerName}</strong></p>
          <Link to="/" className="mt-8 inline-flex h-12 items-center gap-2 rounded-2xl bg-white px-6 text-sm font-black text-slate-950 transition hover:-translate-y-0.5 hover:bg-cyan-100">
            Browse games <AuthIcon name="arrow" className="h-5 w-5" />
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout mode="login">
      <header>
        <p className="text-xs font-black uppercase tracking-[0.3em] text-cyan-200/70">Welcome back, player</p>
        <h1 className="mt-3 text-3xl font-black tracking-[-0.025em] text-white sm:text-4xl">Ready for another round?</h1>
        <p className="mt-3 text-sm leading-6 text-indigo-100/50">Sign in to access your games, tournaments, and play history.</p>
      </header>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <AuthField
          icon="user"
          label="Email or username"
          name="identifier"
          type="text"
          autoComplete="username"
          placeholder="player@example.com"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          required
        />
        <AuthField
          icon="lock"
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Enter your password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          action={(
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="absolute inset-y-0 right-0 grid w-12 place-items-center text-indigo-200/35 transition hover:text-white focus-visible:outline-none focus-visible:text-cyan-200"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <AuthIcon name={showPassword ? "eye-off" : "eye"} />
            </button>
          )}
        />

        {login.isError && <AuthError>{errorMessage}</AuthError>}

        <AuthSubmitButton disabled={login.isPending}>
          {login.isPending ? "Opening your lobby..." : "Enter Pocket Play"}
        </AuthSubmitButton>
      </form>

      <p className="mt-7 text-center text-sm text-indigo-100/45">
        New to the game?{" "}
        <Link to="/register" className="font-bold text-pink-300 transition hover:text-pink-200">Create your player profile</Link>
      </p>
    </AuthLayout>
  );
}
